import axios from 'axios';
import { getNiuHealthBaseUrl, NIU_HEALTH_API_BASE_URL } from 'config/app.config';
import { fGetNiuHealthAccessToken, fGetUserAuthData } from 'lib/appFx';
import { fGetCurrentLanguage } from 'lib/langUtils';

/**
 * Create axios instance for NIU Health API calls
 * Automatically includes NIU Health authentication token
 */
const createNiuHealthApiClient = () => {
  const accessToken = fGetNiuHealthAccessToken();
  
  if (!accessToken) {
    throw new Error('NIU Health access token not found');
  }

  const lang = fGetCurrentLanguage();
  
  return axios.create({
    baseURL: NIU_HEALTH_API_BASE_URL,
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      'Accept-Language': lang,
    },
    timeout: 10000, // 10 seconds timeout
  });
};

/**
 * Fetch user profile details from NIU Health API
 * Replicates the userProfileDetail query from NIU Health's profileApi.js
 * @param {string} profileId - Profile ID from cross-domain cookies
 * @param {string} userType - User type (patient, provider, admin, driver)
 * @returns {Promise<Object>} User profile details with subscription and verification status
 */
export const fetchUserProfileDetail = async (profileId, userType = 'patient') => {
  try {
    if (!profileId) {
      throw new Error('Profile ID is required');
    }

    const apiClient = createNiuHealthApiClient();
    
    // Build relations array based on user type (matching NIU Health's logic)
    const defaultRelations = ['user', 'role', 'language', 'addresses'];
    
    if (userType === 'provider') {
      defaultRelations.push('provider');
    } else {
      defaultRelations.push('patient');
    }

    // Build query string with relations for /profile/{id} endpoint
    const relationParams = defaultRelations.map(rel => `relations[]=${rel}`).join('&');
    const apiUrl = `/profile/${profileId}?${relationParams}`;

    const response = await apiClient.get(apiUrl);
    
    if (response.data && response.data.data) {
      return {
        status: true,
        data: response.data.data
      };
    } else {
      throw new Error('Invalid response format from NIU Health API');
    }
    
  } catch (error) {
    console.error('Error fetching user profile from NIU Health:', error);
    
    // Return error structure compatible with NIU Health's API responses
    return {
      status: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch user profile'
    };
  }
};;

/**
 * Wrapper function that automatically gets user data from cookies and fetches profile
 * @returns {Promise<Object>} User profile details or error
 */
export const fetchCurrentUserProfile = async () => {
  const userAuthData = fGetUserAuthData();
  
  if (!userAuthData) {
    return {
      status: false,
      error: 'User not authenticated'
    };
  }

  return await fetchUserProfileDetail(userAuthData.profileId, userAuthData.userType);
};

/**
 * Check if NIU Health API is accessible and user is authenticated
 * @returns {Promise<boolean>} True if API is accessible, false otherwise
 */
export const checkNiuHealthApiAccess = async () => {
  try {
    const userAuthData = fGetUserAuthData();
    
    if (!userAuthData) {
      return false;
    }

    const apiClient = createNiuHealthApiClient();
    
    // Simple API call to check connectivity using correct /profile/{id} endpoint
    await apiClient.get(`/profile/${userAuthData.profileId}?relations[]=user`);
    
    return true;
  } catch (error) {
    console.error('NIU Health API access check failed:', error);
    return false;
  }
};;